import { useState, useEffect } from "react";
import { Button, Form, Header, Link, SpaceBetween } from "@cloudscape-design/components";
import { useTranslation } from "react-i18next";
import JsonInputNewCasestudyPanel from "./tab-json-input-new-casestudy-panel";
import { DataProvider } from "../../../api/data-provider";
import { useNotification } from "../../../components/Context/NotificationContext";
import { useNavigate, useBeforeUnload } from "react-router-dom";
import { useCasestudies, useUserProfile } from "../../../components";
import { solutionNameOptions_en, solutionNameOptions_cn } from "../../../common/formOptions";
import { categoryDictionary, domainDictionary } from "../../../common/chinese-english-dictionary";
import { v4 as uuidv4 } from "uuid";
import paletteConfig from "../../../config/aws-palette-config.json";

const DEBUG = paletteConfig.debugMode;

/*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
function isJsonDataValid_solutionName(parsedData) {
  const { solutionNameAbbr, solutionName_en, solutionName_cn } = parsedData;
  return (
    solutionNameOptions_en.some((option) => {
      return option.value === solutionNameAbbr && option.label === solutionName_en;
    }) &&
    solutionNameOptions_cn.some((option) => {
      return option.value === solutionNameAbbr && option.label === solutionName_cn;
    })
  );
}

function isJsonDataValid_solutionsUsed(parsedData) {
  const { solutionsUsed_en, solutionsUsed_cn } = parsedData;
  return (
    solutionsUsed_en.every((solution) => {
      return solutionNameOptions_en.some((option) => {
        return option.label === solution;
      });
    }) &&
    solutionsUsed_cn.every((solution) => {
      return solutionNameOptions_cn.some((option) => {
        return option.label === solution;
      });
    })
  );
}

function isJsonDataValid_category(parsedData) {
  const { category_en, category_cn } = parsedData;
  return (
    category_en.every((cat_en, index) => {
      const cat_cn = category_cn[index];
      return categoryDictionary[cat_en] === cat_cn;
    }) &&
    category_cn.every((cat_cn, index) => {
      const cat_en = category_en[index];
      return categoryDictionary[cat_cn] === cat_en;
    })
  );
}

function isJsonDataValid_domain(parsedData) {
  const { domain_en, domain_cn } = parsedData;
  return (
    domain_en.every((d_en, index) => {
      const d_cn = domain_cn[index];
      return domainDictionary[d_en] === d_cn;
    }) &&
    domain_cn.every((d_cn, index) => {
      const d_en = domain_en[index];
      return domainDictionary[d_cn] === d_en;
    })
  );
}

function isJsonDataValid_year(parsedData) {
  const { year } = parsedData;
  const validYears = ["2020", "2021", "2022", "2023", "2024", "2025"];
  return validYears.includes(year);
}

/*****************************************************************************/
function JsonInputBaseFormContent({ content, errorText = null }) {
  const { t } = useTranslation();
  const { setCasestudiesFetched } = useCasestudies();
  const { pushNewNotification } = useNotification();
  const { isAdmin } = useUserProfile();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false); // 提交期间，（1）禁用button；（2）禁止离开页面；
  const [isCasestudyCreateCompleted, setIsCasestudyCreateCompleted] = useState(false);

  // It is an object of casestudy containing all attributes except 'createdBy' which will be automatically generated by the backend lambda before inserting the item into Dynamodb tables.
  const [data, setData] = useState(`{\n\t"solutionNameAbbr": "DTH",\n\t"customerNameAbbr": "Nike" \n}`);

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  useEffect(() => {
    DEBUG && console.log("%cdata", "color: blue; font-weight: bold;", data);
  }, [data]);

  useEffect(() => {
    if (isCasestudyCreateCompleted) {
      const newMessage = {
        type: "success",
        content: t("notification.createCasestudy"),
        dismissible: true,
        dismissLabel: "Dismiss message",
        statusIconAriaLabel: "success",
      };
      pushNewNotification(newMessage);

      console.log("%cNow let's navigate to /casestudies", "color: green; font-weight: bold;");
      setCasestudiesFetched(false);
      setSubmitting(false);
      navigate("/casestudies");
    }
  }, [isCasestudyCreateCompleted]);

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  const onChangeCasestudyHandler = (newData) => {
    setData(newData);
  };

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  async function createNewCasestudy() {
    const parsedData = JSON.parse(data || "{}");
    DEBUG && console.log("%cparsedData (an object of casestudy):", "color: blue; font-weight: bold;", parsedData);

    /*--------------- Step 1: Validate Json Data -------------------*/
    if (!isJsonDataValid_solutionName(parsedData)) {
      const newMessage = {
        type: "warning",
        content: t("notification.createCasestudyJsonDataWarningSolutionName"),
        dismissible: true,
        dismissLabel: "Dismiss message",
        statusIconAriaLabel: "warning",
      };
      pushNewNotification(newMessage);
      setSubmitting(false);
      return;
    }

    if (!isJsonDataValid_solutionsUsed(parsedData)) {
      const newMessage = {
        type: "warning",
        content: t("notification.createCasestudyJsonDataWarningSolutionsUsed"),
        dismissible: true,
        dismissLabel: "Dismiss message",
        statusIconAriaLabel: "warning",
      };
      pushNewNotification(newMessage);
      setSubmitting(false);
      return;
    }

    if (!isJsonDataValid_category(parsedData)) {
      const newMessage = {
        type: "warning",
        content: t("notification.createCasestudyJsonDataWarningCategory"),
        dismissible: true,
        dismissLabel: "Dismiss message",
        statusIconAriaLabel: "warning",
      };
      pushNewNotification(newMessage);
      setSubmitting(false);
      return;
    }

    if (!isJsonDataValid_domain(parsedData)) {
      const newMessage = {
        type: "warning",
        content: t("notification.createCasestudyJsonDataWarningDomain"),
        dismissible: true,
        dismissLabel: "Dismiss message",
        statusIconAriaLabel: "warning",
      };
      pushNewNotification(newMessage);
      setSubmitting(false);
      return;
    }

    if (!isJsonDataValid_year(parsedData)) {
      const newMessage = {
        type: "warning",
        content: t("notification.createCasestudyJsonDataWarningYear"),
        dismissible: true,
        dismissLabel: "Dismiss message",
        statusIconAriaLabel: "warning",
      };
      pushNewNotification(newMessage);
      setSubmitting(false);
      return;
    }

    /*--------------- Step 2: Update the DynamoDB -------------------*/
    // 写后端数据库之前，为每个casestudy生成一个唯一的id
    parsedData.id = uuidv4();
    try {
      const response = await new DataProvider().createCasestudy(parsedData);
      DEBUG && console.log("%cApiCallCompleted, set 'isCasestudyCreateCompleted' to 'true' now.", "color: green; font-weight: bold;", response);
      setIsCasestudyCreateCompleted(true);
    } catch (error) {
      console.error("Create Casestudy failed:", error);
      throw error; // 抛出错误，让外部的 onSubmitHandler 的 catch 块捕获并统一处理，不在这里进行处理
    }
  }

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  const onSubmitHandler = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await createNewCasestudy();
    } catch (error) {
      console.error("Error encountered while executing `await createNewCasestudy();` ", error);
      const newMessage = {
        type: "error",
        content: t("notification.createOrUpdateCasestudyApiError"),
        dismissible: true,
        dismissLabel: "Dismiss message",
        statusIconAriaLabel: "error",
      };
      pushNewNotification(newMessage);
      setSubmitting(false);
    }
  };

  const onCancelClick = () => {
    navigate("/casestudies");
  };

  // 点击按钮提交期间，阻止刷新和关闭
  useBeforeUnload((event) => {
    if (submitting) {
      event.preventDefault();
      event.returnValue = t("useBeforeUnload");
    }
  });

  /*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
  return (
    <form>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancelClick}>
              {t("casestudies:CasestudyCreateForm.cancelButton")}
            </Button>
            <Button variant="primary" onClick={onSubmitHandler} loading={submitting} disabled={!isAdmin || submitting}>
              {submitting ? t("casestudies:CasestudyCreateForm.createCasestudyButtonSubmitting") : t("casestudies:CasestudyCreateForm.createCasestudyButton")}
            </Button>
          </SpaceBetween>
        }
        errorText={errorText}
        errorIconAriaLabel="Error"
      >
        {content({ onChangeCasestudyHandler, data })}
      </Form>
    </form>
  );
}

export function JsonInputFormContent({ loadHelpPanelContent }) {
  return (
    <JsonInputBaseFormContent
      content={({ onChangeCasestudyHandler, data }) => (
        <SpaceBetween size="l">
          <JsonInputNewCasestudyPanel loadHelpPanelContent={loadHelpPanelContent} onChangeCasestudyHandler={onChangeCasestudyHandler} data={data} />
        </SpaceBetween>
      )}
    />
  );
}
